/**
 * One request in flight, shared by everybody who asks while it is.
 *
 * For a read several independent callers need during the same render — the
 * signed-in customer, which the route guard, the layout and a page each
 * resolved for themselves, one after the other, three WHMCS round-trips per
 * page view. The first caller starts it; the rest are handed the same promise;
 * once it settles, the next caller starts a new one. A failure is shared with
 * the callers who were waiting on it and is NOT remembered: the caller after
 * them tries again.
 *
 * Create it where its lifetime should be the lifetime of the thing it guards. A
 * setup store's closure is per store instance — on the server, one per request
 * — which is what keeps one customer's read from ever answering another's.
 */
export function createSingleFlight<T>() {
  let inFlight: Promise<T> | null = null

  return {
    run(task: () => Promise<T>): Promise<T> {
      if (inFlight) return inFlight

      const request = task()
      inFlight = request
      // Both handlers, so this bookkeeping never rejects on its own: the failure
      // belongs to whoever awaits `request`.
      const settle = () => {
        if (inFlight === request) inFlight = null
      }
      request.then(settle, settle)
      return request
    },
    get pending(): boolean {
      return inFlight !== null
    },
  }
}
